import React from 'react';
import { Avatar as Avtr } from 'antd';
import { getString } from '@nackle/intl-tools';

/**
 * A component that displays an avatar with associated user information
 *
 * {@link https://ant.design/components/avatar#avatar}
 *
 * @module AvatarInfo
 * @param {Object} props - The component props
 * @param {React.ReactNode} props.children - Child elements to render inside the avatar
 * @param {string} props.firstName - User's first name
 * @param {string} props.lastName - User's last name
 * @param {string} [props.pronouns] - User's preferred pronouns
 * @param {React.ReactNode} [props.metaData] - Additional metadata to display below the name
 * @param {('mini'|'small'|'medium'|'large')} [props.size='medium'] - Size variant of the avatar
 * @param {string} [props.className] - Additional CSS class to apply to avatar
 * @param {boolean} [props.isUser] - Whether this avatar represents the current user
 * @returns {React.ReactElement} Rendered avatar with user info
 */
const AvatarInfo = ( { children, ...props } ) => {
    const { firstName, lastName, pronouns, metaData, size, className, isUser, ...restProps } = props;

    return (
        <div className='avatar-container'>
            { ( () => {
                switch ( size ) {
                    case 'mini':
                        return <Avtr { ...restProps } size={ 24 } className={ className ? `mini-avatar ${ className }` : 'mini-avatar' }>
                            { children }
                        </Avtr>;
                    case 'small':
                        return <Avtr { ...restProps } size={ 36 } className={ className ? `small-avatar ${ className }` : 'small-avatar' }>
                            { children }
                        </Avtr>;
                    case 'medium':
                        return <Avtr { ...restProps } size={ 48 } className={ className ? `medium-avatar ${ className }` : 'medium-avatar' }>
                            { children }
                        </Avtr>;
                    case 'large':
                        return <Avtr { ...restProps } size={ 64 } className={ className ? `large-avatar ${ className }` : 'large-avatar' }>
                            { children }
                        </Avtr>;
                    default:
                        return <Avtr { ...restProps } size={ size || 48 } className={ className ? `avatar ${ className }` : 'avatar' }>
                            { children }
                        </Avtr>;
                }
            } )() }
            <div className='avatar-info-container'>
                <div className={ size === 'mini' ? 'avatar-info-name-mini' : 'avatar-info-name' }>
                    <span>{ firstName }</span>&nbsp;
                    <span>{ lastName }</span>&nbsp;
                    {
                        pronouns &&
                            <div className='avatar-info-pronouns'>
                                ({ `${ pronouns }` })
                            </div>
                    }
                    {
                        isUser &&
                            <div className='avatar-info-user'>
                                { getString( 'origami.avatar.you' ) }
                            </div>
                    }

                </div>
                <div className='avatar-info-content'>
                    { metaData }
                </div>
            </div>
        </div>
    );
};

export default AvatarInfo;


